import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher';
import { UserIcon, CogIcon } from '../ui/Icons';
import CombinedSearch from './CombinedSearch';
import { NotificationBell } from '../NotificationCenter';
import UserAvatar, { getUserDisplayName } from '../users/UserAvatar';
import { cn } from '../../lib/utils';
import { useIsCompact } from '../../hooks/useBreakpoint';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { AnchoredMenu } from '../ui/AnchoredMenu';
import { docsHomeUrl } from '../../config/docsConfig';

interface TopbarProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  hideThemeSwitcher?: boolean;
  hideSearch?: boolean;
   /** When true (used by mobile layout), keep only Sign out in user menu and hide extra action buttons */
   simpleUserMenu?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  title,
  subtitle: staticSubtitle,
  icon,
  onToggleMobileMenu,
  isMobileMenuOpen = false,
  hideThemeSwitcher = false,
  hideSearch = false,
   simpleUserMenu = false,
}) => {
  const { subtitle: dynamicSubtitle, titleOverride, rightActions } = useTopbar();
  const subtitle = dynamicSubtitle || staticSubtitle;
  const displayTitle = titleOverride ?? title;
  const { logout, user } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('topbar');
  const isCompact = useIsCompact();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const userMenuBtnRef = useRef<HTMLButtonElement>(null);
  const isAdmin = user?.role === 'ADMIN';

  const displayName = user ? getUserDisplayName(user) : '';

  // Update document title with app name
  useDocumentTitle(displayTitle ?? title);

  const handleLogout = async () => {
    await logout();
  };

  const addMenuGroups = [
    { label: 'Work', items: [
        { label: 'Todo', href: '/todos/new', icon: 'todo', iconBg: 'bg-emerald-100 dark:bg-emerald-500/20', iconColor: 'text-emerald-600 dark:text-emerald-400' },
      ] },
    { label: 'Communication', items: [
        { label: 'New Email', href: '/emails/compose', icon: 'email', iconBg: 'bg-amber-100 dark:bg-amber-500/20', iconColor: 'text-amber-600 dark:text-amber-400' },
      ] },
    { label: 'CRM', items: [
        { label: 'Client', href: '/clients/new', icon: 'client', iconBg: 'bg-violet-100 dark:bg-violet-500/20', iconColor: 'text-violet-600 dark:text-violet-400' },
        { label: 'Appointment', href: '/appointments/new', icon: 'appointment', iconBg: 'bg-sky-100 dark:bg-sky-500/20', iconColor: 'text-sky-600 dark:text-sky-400' },
      ] },
  ];
  const filteredAddMenuGroups = addMenuGroups;

  return (
    <header className={`${colors.topbarBackground} shadow-sm border-b-2 ${colors.topbarBorder} sticky top-0 z-30`}>
      <div className={`flex items-center justify-between ${isCompact ? 'px-3 py-3' : 'px-6 py-4'}`}>
        {/* Left section: Menu toggle, icon, title */}
        <div className={`flex items-center ${isCompact ? 'space-x-2' : 'space-x-3'} min-w-0 flex-shrink-0`}>
          {onToggleMobileMenu && (
            <button
              onClick={onToggleMobileMenu}
              className={cn('lg:hidden p-2 rounded-md', colors.textMuted, colors.hoverSidebarText, colors.hoverSidebarActive, 'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white')}
              aria-label={isMobileMenuOpen ? t('closeMenu') : t('openMenu')}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
          {icon && (
            <div className={`${colors.textSecondary} ${isCompact ? 'flex-shrink-0' : ''}`}>
              {icon}
            </div>
          )}
          {displayTitle ? (
            <div className="min-w-0">
              <h1 className={`${isCompact ? 'text-lg' : 'text-2xl'} font-bold ${colors.text} truncate`}>
                {displayTitle}
              </h1>
              {subtitle && !isCompact && (
                <p className={`text-sm ${colors.textSecondary} mt-0.5 truncate`}>
                  {subtitle}
                </p>
              )}
            </div>
          ) : null}
        </div>
        
        {/* Center section: Combined search (pages + records) */}
        <div className="flex-1 flex justify-center items-center gap-4 min-w-0 px-4">
          <CombinedSearch />
        </div>
        
        {/* Right section: Add dropdown, Search, dropdown favorites, user menu */}
        <div className={`flex items-center ${isCompact ? 'space-x-2' : 'space-x-4'} flex-shrink-0`}>
          {/* Add dropdown (hidden in simpleUserMenu/mobile mode) */}
          {!simpleUserMenu && (
            <div className="relative">
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                className={cn(
                  'relative flex items-center justify-center rounded-lg transition-colors',
                  isCompact ? 'p-2 min-w-[44px] min-h-[44px]' : 'p-2',
                  colors.textMuted,
                  colors.hoverTextSecondary,
                  'hover:bg-black/5 dark:hover:bg-white/5',
                  'focus:outline-none focus:ring-2',
                  colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : 'focus:ring-indigo-500'
                )}
                title="Quick add"
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <svg className={cn('w-4 h-4 ml-0.5 shrink-0 transition-transform', showAddMenu && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showAddMenu && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden onClick={() => setShowAddMenu(false)} />
                  <div
                    className={cn(
                      'absolute left-0 mt-2.5 w-72 z-20 overflow-hidden',
                      'rounded-2xl shadow-2xl border backdrop-blur-xl',
                      'animate-in fade-in slide-in-from-top-2 duration-200',
                      colors.surface,
                      colors.border
                    )}
                  >
                    <div className={cn('px-4 pt-4 pb-3', colors.border, 'border-b')}>
                      <div className="flex items-center gap-2">
                        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', colors.surfaceHover, colors.primaryFg)}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </span>
                        <div>
                          <p className={cn('text-sm font-bold', colors.text)}>Create new</p>
                          <p className={cn('text-xs', colors.textMuted)}>Todo, email, or CRM item</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      {filteredAddMenuGroups.map((group) => (
                        <div key={group.label} className="mb-3 last:mb-0">
                          <p className={cn('px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest', colors.textMuted)}>
                            {group.label}
                          </p>
                          <div className="space-y-0.5">
                            {group.items.map((item) => (
                              <button
                                key={item.label}
                                type="button"
                                onClick={() => {
                                  setShowAddMenu(false);
                                  navigate(item.href);
                                }}
                                className={cn(
                                  'flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                                  'border-l-2 border-transparent',
                                  colors.hoverBorderPrimary,
                                  colors.text,
                                  colors.interactiveHover
                                )}
                              >
                                <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', item.iconBg, item.iconColor)}>
                                {item.icon === 'task' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                  </svg>
                                )}
                                {item.icon === 'support' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                  </svg>
                                )}
                                {item.icon === 'todo' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                  </svg>
                                )}
                                {item.icon === 'client' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                )}
                                {item.icon === 'appointment' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                )}
                                {item.icon === 'email' && (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                )}
                                </span>
                                <span>{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {!simpleUserMenu && rightActions && (
            <div className="flex items-center gap-2 shrink-0">{rightActions}</div>
          )}

          {!simpleUserMenu && <NotificationBell />}

          {!simpleUserMenu && (
            <button
              type="button"
              onClick={() => {
                window.open(docsHomeUrl(i18n.language), '_blank', 'noopener,noreferrer');
              }}
              className={cn(
                'relative flex items-center justify-center rounded-lg transition-colors',
                isCompact ? 'p-2 min-w-[44px] min-h-[44px]' : 'p-2',
                colors.textMuted,
                colors.hoverTextSecondary,
                'hover:bg-black/5 dark:hover:bg-white/5',
                'focus:outline-none focus:ring-2',
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : 'focus:ring-indigo-500',
              )}
              title={t('helpTitle')}
              aria-label={t('help')}
            >
              <HelpCircle className="w-5 h-5 shrink-0" strokeWidth={1.75} aria-hidden />
            </button>
          )}

          {/* Theme Switcher - only show if enabled and not hidden (e.g. mobile layout) */}
          {!hideThemeSwitcher && <ThemeSwitcher />}

          {/* User Menu */}
          <div className="relative">
            <button
              ref={userMenuBtnRef}
              type="button"
              onClick={() => setShowUserMenu((open) => !open)}
              className={cn(
                'flex items-center rounded-lg bg-transparent hover:bg-black/5 dark:hover:bg-white/5 focus:outline-none focus:ring-2',
                isCompact ? 'space-x-1 p-1.5 min-w-[36px] min-h-[36px]' : 'space-x-2 p-1.5',
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : 'focus:ring-indigo-500'
              )}
              aria-expanded={showUserMenu}
              aria-haspopup="menu"
              aria-label={displayName || t('accountSettings')}
            >
              {user ? (
                <UserAvatar
                  userId={user.id}
                  photoPath={user.userPhotoPath}
                  userFirstName={user.userFirstName}
                  userLastName={user.userLastName}
                  userEmail={user.userEmail}
                  userDisplayName={user.userDisplayName}
                  size="sm"
                />
              ) : (
                <div className={cn('h-8 w-8 rounded-full flex items-center justify-center', colors.primary, colors.primaryText)}>
                  <span className="font-semibold text-xs">U</span>
                </div>
              )}
              {!isCompact && displayName && (
                <span className={cn('hidden md:block max-w-[9rem] truncate text-sm font-medium', colors.text)}>
                  {displayName}
                </span>
              )}
              <svg className={`h-3.5 w-3.5 ${colors.textMuted} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnchoredMenu
              open={showUserMenu}
              onClose={() => setShowUserMenu(false)}
              anchorRef={userMenuBtnRef}
              align="end"
              className={cn('w-60', colors.surface, colors.border)}
            >
              {user && (
                <div className={cn('px-4 py-3 border-b', colors.border)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <UserAvatar
                      userId={user.id}
                      photoPath={user.userPhotoPath}
                      userFirstName={user.userFirstName}
                      userLastName={user.userLastName}
                      userEmail={user.userEmail}
                      userDisplayName={user.userDisplayName}
                      size="md"
                      rounded="xl"
                    />
                    <div className="min-w-0 flex-1">
                      <p className={cn('text-sm font-semibold truncate', colors.text)}>{displayName}</p>
                      <p className={cn('text-xs truncate', colors.textMuted)}>{user.userEmail}</p>
                      {user.role === 'ADMIN' && (
                        <p className={cn('text-[10px] font-medium uppercase tracking-wide mt-1', colors.textMuted)}>
                          Administrator
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!simpleUserMenu && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/preferences');
                    }}
                    className={cn('flex items-center gap-2 w-full text-left px-4 py-2 text-sm', colors.textSecondary, colors.interactiveHover)}
                  >
                    <UserIcon className="w-4 h-4" />
                    {t('accountSettings')}
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/settings');
                      }}
                      className={cn('flex items-center gap-2 w-full text-left px-4 py-2 text-sm', colors.textSecondary, colors.interactiveHover)}
                    >
                      <CogIcon className="w-4 h-4" />
                      {t('applicationSettings')}
                    </button>
                  )}
                  <hr className={`my-1 ${colors.border}`} />
                </>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowUserMenu(false);
                  void handleLogout();
                }}
                className={cn('flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600', colors.interactiveHover)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('signOut')}
              </button>
            </AnchoredMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;

